import React, { useCallback, useContext, useReducer, Reducer } from 'react';

import { useEscKey } from '../../../tools/keyboardTools';
import { IOverlayProps, Overlay } from './Overlay';

interface IOverlayContext {
    isVisible: boolean;
    showOverlay: <T>(config: IOverlayProps<T>) => void;
    hideOverlay: () => void;
}

interface IOverlayState {
    isVisible: boolean;
    config: IOverlayProps;
}

export const OverlayContext = React.createContext<IOverlayContext>({
    isVisible: false,
    // tslint:disable-next-line:no-empty
    showOverlay: (_: IOverlayProps) => { },
    // tslint:disable-next-line:no-empty
    hideOverlay: () => { }
});

export const initialState: IOverlayState = {
    isVisible: false,
    // tslint:disable-next-line:no-null-keyword
    config: null
};

type Action =
    | { type: 'SHOW_OVERLAY'; config: IOverlayProps }
    | { type: 'HIDE_OVERLAY' };

export const reducer: Reducer<IOverlayState, Action> = (state, action) => {
    switch (action.type) {
        case 'SHOW_OVERLAY':
            return {
                ...state,
                isVisible: true,
                config: action.config
            };
        default:
            return { ...initialState };
    }
};

export const useOverlayContext = () => {
    return useContext(OverlayContext);
};

export const OverlayContextProvider = ({ children }) => {
    const [state, dispatch] = useReducer(reducer, { ...initialState });

    const showHandler = useCallback(<T, >(config: IOverlayProps<T>): void => {
        dispatch({ type: 'SHOW_OVERLAY', config });
    }, []);

    const hideHandler = useCallback((): void => {
        dispatch({ type: 'HIDE_OVERLAY' });
    }, []);

    useEscKey(() => hideHandler());

    return (
        <OverlayContext.Provider
            value={{
                isVisible: state.isVisible,
                showOverlay: showHandler,
                hideOverlay: hideHandler
            }}
        >
            {state.isVisible && <Overlay {...state.config} />}
            {children}
        </OverlayContext.Provider>
    );
};
